import React, { useMemo } from 'react'
import CarouselCard from 'components/OfferList/OfferCards/CarouselCard'
import CruisePriceDetails from './CruisePriceDetails'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton'
import { getLocationString } from 'checkout/lib/utils/accommodation/location'
import { VIRGIN_VELOCITY_PARTNERSHIP_CODE } from 'constants/partnerships'
import { connect } from 'react-redux'
import { unique } from 'lib/array/arrayUtils'
import useIsHomepage from 'home/hooks/isHomepage'
import CruiseDescription from './CruiseDescription'
import TourV2UrgencyTags from 'components/Tours/TourV2UrgencyTags'
import { getCruiseOfferPriceVariation } from 'lib/cruises/cruiseUtils'

interface Props {
  offer: App.CruiseOffer;
  onClick: () => void;
  isVelocityEnabled: boolean;
  className?: string;
  offerUrl: string;
}

function CruiseOfferCard({ offer, onClick, isVelocityEnabled, className, offerUrl }: Props) {
  const isHomepage = useIsHomepage()
  const { lowestOverallPriceDetails } = offer.mainDepartureDetails
  const velocityFeatures = (isVelocityEnabled && !isHomepage) ? { canEarn: true, canBurn: true } : undefined

  const portLength = useMemo(() => {
    const ports = offer.itinerary?.map((itinerary: App.CruiseItineraryItem) => {
      if (!itinerary.port) return
      return (itinerary.port.city || itinerary.port.name.split(',')[0]).trim()
    }).filter(Boolean) as string[]

    return unique(ports).length
  }, [offer.itinerary])

  const countryLength = useMemo(() => {
    const countries = offer.itinerary?.map((itinerary: App.CruiseItineraryItem) => {
      if (!itinerary.port) return
      return itinerary.port.countryCode
    }).filter(Boolean) as string[]

    return unique(countries).length
  }, [offer.itinerary])

  const discountPills = useMemo(() => {
    if (lowestOverallPriceDetails.discountPills) {
      return {
        totalPrice: lowestOverallPriceDetails.discountPills.price,
        percentage: lowestOverallPriceDetails.discountPills.discountPercentage,
      }
    }

    return undefined
  }, [lowestOverallPriceDetails.discountPills])

  const priceDetails = useMemo(() => {
    const priceVariation =
      getCruiseOfferPriceVariation(offer, lowestOverallPriceDetails)

    return {
      saleUnit: priceVariation.primaryPrice.type,
      totalPrice: priceVariation.primaryPrice.total,
    }
  }, [offer, lowestOverallPriceDetails])

  return (
    <CarouselCard
      className={className}
      bookmarkButton={<BookmarkButton offer={offer} />}
      image={offer.images[0]}
      location={getLocationString(offer)}
      title={offer.name}
      offerUrl={offerUrl}
      productType={offer.productType}
      velocityFeatures={velocityFeatures}
      onClick={onClick}
      urgencyLabels={<TourV2UrgencyTags tags={offer.urgencyTags} />}
      offerPriceDetails={
        <CruisePriceDetails
          priceDetails={priceDetails}
          discountPills={discountPills} />
      }
      description={
        <CruiseDescription
          portLength={portLength}
          countryLength={countryLength}
          duration={offer.duration}
          shipName={offer.ship.name}
          cruiseLineName={offer.cruiseLine.name} />
      }
    />
  )
}

function mapStateToProps(state: App.State) {
  return {
    isVelocityEnabled: state.geo.partnership?.code === VIRGIN_VELOCITY_PARTNERSHIP_CODE,
  }
}

export default connect(mapStateToProps)(CruiseOfferCard)
